import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    TableCell,
    TableRow,
    TableSortLabel,
    CircularProgress,
    IconButton,
    Tooltip,
    Snackbar,
    Alert,
    Switch
} from '@mui/material';
import { Edit, Visibility } from '@mui/icons-material';
import { AppButton } from '../../../shared/components/AppButton';
import { AppTable } from '../../../shared/components/AppTable';
import { AppTablePagination } from '../../../shared/components/AppTablePagination';
import { AppSearch } from '../../../shared/components/AppSearch';
import { getCmsPages, toggleCmsPageActive } from '../../../shared/services/cmsService';
import { CmsPageAttributes } from '../../../shared/types/cms';
import moment from 'moment';

export const CmsPages: React.FC = () => {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [searchInput, setSearchInput] = useState('');
    const [currentPage, setCurrentPage] = useState(0);
    const [totalRows, setTotalRows] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [pages, setPages] = useState<CmsPageAttributes[]>([]);
    const [sortData, setSortData] = useState<{ active: string; direction: 'asc' | 'desc' } | null>(null);
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({ open: false, message: '', severity: 'info' });

    const fetchPages = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await getCmsPages({
                page: currentPage + 1,
                limit: pageSize,
                search: searchInput || undefined
            });

            if (response.success) {
                // The API returns { data: { data: [...], pagination: { totalItems: N } } }
                const cmsData = response.data;
                let pagesList = cmsData?.data || [];

                // Filter by search locally if API returns all data (though the API usually handles this)
                if (searchInput) {
                    const searchLower = searchInput.toLowerCase();
                    pagesList = pagesList.filter(
                        (p) =>
                            p.title.toLowerCase().includes(searchLower) ||
                            p.slug.toLowerCase().includes(searchLower)
                    );
                }

                setPages(pagesList);
                setTotalRows(cmsData?.pagination?.totalItems || pagesList.length);
            }
        } catch (error: any) {
            console.error('Error fetching CMS pages:', error);
            setSnackbar({ open: true, message: 'Error loading CMS pages', severity: 'error' });
            setPages([]);
            setTotalRows(0);
        } finally {
            setIsLoading(false);
        }
    }, [currentPage, pageSize, searchInput]);

    useEffect(() => {
        fetchPages();
    }, [currentPage, pageSize]);

    const handleToggleActive = async (slug: string, currentStatus?: boolean) => {
        const originalStatus = !!currentStatus;
        setPages(prev => prev.map(p => (p.slug === slug ? { ...p, isActive: !originalStatus } : p)));
        try {
            const response = await toggleCmsPageActive(slug);
            if (!response.success) {
                setPages(prev => prev.map(p => (p.slug === slug ? { ...p, isActive: originalStatus } : p)));
                setSnackbar({ open: true, message: response.message || 'Failed to update status', severity: 'error' });
            } else {
                setSnackbar({ open: true, message: response.message || 'Status updated', severity: 'success' });
            }
        } catch (error: any) {
            setPages(prev => prev.map(p => (p.slug === slug ? { ...p, isActive: originalStatus } : p)));
            setSnackbar({ open: true, message: error.response?.data?.message || 'Failed to update status', severity: 'error' });
        }
    };

    const handleSearchChange = (value: string) => {
        setSearchInput(value);
        setCurrentPage(0);
    };

    const handleSort = (property: string) => {
        const isAsc = sortData?.active === property && sortData?.direction === 'asc';
        setSortData({
            active: property,
            direction: isAsc ? 'desc' : 'asc'
        });
    };

    const handlePageChange = (event: React.ChangeEvent<unknown>, newPage: number) => {
        setCurrentPage(newPage - 1);
    };

    // Sort pages locally
    const sortedPages = [...pages].sort((a, b) => {
        if (!sortData?.active) return 0;
        const key = sortData.active as keyof CmsPageAttributes;
        const aVal = (a[key] || '').toString().toLowerCase();
        const bVal = (b[key] || '').toString().toLowerCase();
        if (sortData.direction === 'asc') {
            return aVal.localeCompare(bVal);
        }
        return bVal.localeCompare(aVal);
    });

    return (
        <div className="flex flex-col w-full min-h-full bg-white">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-6 sm:py-8 sm:px-10 border-b border-gray-200 bg-white">
                {isLoading && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <CircularProgress />
                    </div>
                )}
                <div className="flex-1 min-w-0">
                    <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight text-gray-900">
                        CMS Pages
                    </h2>
                </div>
                <div className="flex items-center gap-4 mt-4 sm:mt-0 flex-wrap">
                    <AppSearch
                        value={searchInput}
                        onChange={handleSearchChange}
                        onSearch={fetchPages}
                        placeholder="Search"
                        className="w-full sm:w-64"
                    />
                </div>
            </div>

            {/* Table */}
            <div className="flex flex-1 flex-col p-6 sm:p-10 overflow-y-auto">
                <div className="flex flex-col w-full flex-1">
                    <AppTable
                        head={
                            <TableRow className="bg-gray-100">
                                <TableCell className="!font-semibold !text-gray-700">No</TableCell>
                                <TableCell className="!font-semibold !text-gray-700">
                                    <TableSortLabel
                                        active={sortData?.active === 'title'}
                                        direction={sortData?.active === 'title' ? sortData.direction : 'asc'}
                                        onClick={() => handleSort('title')}
                                    >
                                        Title
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell className="!font-semibold !text-gray-700">
                                    <TableSortLabel
                                        active={sortData?.active === 'slug'}
                                        direction={sortData?.active === 'slug' ? sortData.direction : 'asc'}
                                        onClick={() => handleSort('slug')}
                                    >
                                        Slug
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell className="!font-semibold !text-gray-700">Meta Title</TableCell>
                                <TableCell className="!font-semibold !text-gray-700">Created At</TableCell>
                                <TableCell className="!font-semibold !text-gray-700">Active</TableCell>
                                <TableCell className="!font-semibold !text-gray-700" width={220}>Actions</TableCell>
                            </TableRow>
                        }
                        isEmpty={sortedPages.length === 0}
                        emptyColSpan={7}
                    >
                        {sortedPages.map((item, i) => (
                            <TableRow
                                key={item.id}
                                className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                            >
                                <TableCell className="!text-gray-700">{(currentPage * pageSize) + i + 1}</TableCell>
                                <TableCell className="!text-gray-900">{item.title}</TableCell>
                                <TableCell className="!text-gray-700">{item.slug}</TableCell>
                                <TableCell className="!text-gray-700">{item.metaTitle || '-'}</TableCell>
                                <TableCell className="!text-gray-700">
                                    {moment(item.createdAt).format('DD MMM YYYY')}
                                </TableCell>
                                <TableCell>
                                    <Switch
                                        checked={!!item.isActive}
                                        onChange={() => handleToggleActive(item.slug, item.isActive)}
                                        color="primary"
                                        size="small"
                                    />
                                </TableCell>
                                <TableCell>
                                    <div className="flex items-center gap-2">
                                        <AppButton
                                            variant="secondary"
                                            size="small"
                                            startIcon={<Visibility className="!w-4 !h-4" />}
                                            onClick={() => navigate(`/cms/view/${item.slug}`)}
                                        >
                                            View
                                        </AppButton>
                                        <AppButton
                                            variant="primary"
                                            size="small"
                                            startIcon={<Edit className="!w-4 !h-4" />}
                                            onClick={() => navigate(`/cms/edit/${item.slug}`)}
                                        >
                                            Edit
                                        </AppButton>
                                    </div>
                                </TableCell>
                            </TableRow>
                        ))}
                    </AppTable>
                </div>

                <AppTablePagination
                    totalRows={totalRows}
                    page={currentPage}
                    pageSize={pageSize}
                    onPageChange={handlePageChange}
                    onPageSizeChange={(size) => setPageSize(size)}
                />
            </div>

            {/* Snackbar */}
            <Snackbar open={snackbar.open} autoHideDuration={2000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
            </Snackbar>
        </div>
    );
};
